import type { ChatbotLinkHandler } from "../types";

/**
 * Scheme-based handler for `indigo://ama-guide/...` links emitted by the
 * AMA-guide agent tool. Canonical form is `indigo://ama-guide/page/{N}`,
 * but we parse leniently — the first positive integer we find in the
 * path segments or the `page` / `p` query params wins. The goal is to
 * stay clickable even if the LLM paraphrases the URL shape; as long as
 * the scheme prefix and a page number survive, the click still works.
 *
 * Tools emit full markdown links in the content stream (e.g.
 * `[page 42](indigo://ama-guide/page/42)`), so there's no `linkify` pass
 * here — that bare-page heuristic was the old `amaPageHandler` and has
 * been removed.
 */
const AMA_GUIDE_SCHEME_PREFIX = "indigo://ama-guide";

function pageFromHref(href: string): number | null {
  if (!href.toLowerCase().startsWith(AMA_GUIDE_SCHEME_PREFIX)) return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  // Try query params first (`?page=42` or `?p=42`), then walk the path
  // segments (`/page/42`, `/42`) so both shapes are accepted.
  const candidates: (string | null)[] = [
    url.searchParams.get("page"),
    url.searchParams.get("p"),
    ...url.pathname.split("/"),
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}

export const amaGuideSchemeHandler: ChatbotLinkHandler = {
  id: "ama-guide-scheme",
  matches: (href) => pageFromHref(href) !== null,
  onClick: (href, ctx) => {
    const page = pageFromHref(href);
    if (page === null) return;
    if (!ctx.onRequestAmaGuide) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[chatbotLinks] AMA Guide scheme link clicked but no onRequestAmaGuide handler is wired:",
          href,
        );
      }
      return;
    }
    // Pure page navigation — no highlight. The viewer seeds its search
    // bar from `highlight`, so sending "page 42" would make the user
    // think they're in search mode and every "page 42" string mention
    // would get highlighted. A bare page click just jumps.
    void ctx.onRequestAmaGuide({ page });
  },
};
